import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  map,
  Observable,
  take,
} from 'rxjs';
import {
  CreateSigneringCommand,
  SigneringClient,
} from '../clients/signering.client';

const STATUS_POLLING_INTERVALL_MS = 2000;
const MAX_POLLING_ATTEMPTS = 5;

@Injectable({
  providedIn: 'root',
})
export class SigneringService {
  private signeringStatusSubject = new BehaviorSubject<
    EgreementStatesEnum | undefined
  >(undefined);
  signeringStatusChange = this.signeringStatusSubject.asObservable();

  private signeringServiceErrorSubject = new BehaviorSubject<
    SigneringServiceError[]
  >([]);
  signeringServiceError$ = this.signeringServiceErrorSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  private pollingStatusCount = signal(0);

  constructor(private signeringClient: SigneringClient) {}

  createSignering(command: CreateSigneringCommand): void {
    this.isLoadingSubject.next(true);
    this.signeringClient.createSignering(command).subscribe({
      next: (response: { redirectUrl: string }): void => {
        if (response && response.redirectUrl) {
          this.signeringStatusSubject.next(EgreementStatesEnum.DRAFT);
          globalThis.location.href = response.redirectUrl;
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error creating signering:', error);
        this.signeringServiceErrorSubject.next([
          SigneringServiceErrorEnum.DirektsigneringNotAvailable,
        ]);
        this.isLoadingSubject.next(false);
      },
    });
  }

  getSigneradDokument(rapportId: string): Observable<PPGResponseData> {
    this.isLoadingSubject.next(true);
    return this.signeringClient.getSignedDocument(rapportId).pipe(
      map(
        (response: GetSignedDocumentResponse): PPGResponseData =>
          this.mapToPPGResponseData(response),
      ),
      catchError((error: HttpErrorResponse): Observable<PPGResponseData> => {
        console.error('Error fetching signed document.', error);
        this.signeringServiceErrorSubject.next([
          SigneringServiceErrorEnum.DirektsigneringNotAvailable,
        ]);
        this.isLoadingSubject.next(false);
        throw error;
      }),
    );
  }

  async deleteSigneradDokument(rapportId: string): Promise<void> {
    const signeringStatus = await this.getSigneringStatus(rapportId);
    if (!signeringStatus) return;
    this.signeringClient.deleteSignedDocument(rapportId);
    this.signeringStatusSubject.next(undefined);
  }

  async checkSigneringStatus(rapportId: string): Promise<void> {
    const statusResponse = await this.getSigneringStatus(rapportId);
    if (!statusResponse) return;
    switch (statusResponse.status) {
      case EgreementStatesEnum.SIGNED_BY_ALL: {
        this.signeringStatusSubject.next(EgreementStatesEnum.SIGNED_BY_ALL);
        break;
      }
      case EgreementStatesEnum.READY_TO_SIGN: {
        if (this.pollingStatusCount() < MAX_POLLING_ATTEMPTS) {
          setTimeout((): void => {
            this.checkSigneringStatus(rapportId);
          }, STATUS_POLLING_INTERVALL_MS);
        }
        this.pollingStatusCount.set(this.pollingStatusCount() + 1);
        break;
      }
      case EgreementStatesEnum.DRAFT: {
        setTimeout((): void => {
          this.checkSigneringStatus(rapportId);
        }, STATUS_POLLING_INTERVALL_MS);
        break;
      }
    }
  }

  setIsLoading(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  private async getSigneringStatus(
    rapportId: string,
  ): Promise<SigneringStatusResponse> {
    const response$ = this.signeringClient.getSigneringStatus(rapportId).pipe(
      take(1),
      catchError(
        (error: HttpErrorResponse): Promise<SigneringStatusResponse> => {
          console.error('Error fetching signering status.', error);
          this.signeringServiceErrorSubject.next([
            SigneringServiceErrorEnum.DirektsigneringNotAvailable,
          ]);
          throw error;
        },
      ),
    );
    return await firstValueFrom(response$);
  }

  private mapToPPGResponseData(
    response: GetSignedDocumentResponse,
  ): PPGResponseData {
    // Decode base64
    const byteCharacters = atob(response.documentContent);
    // eslint-disable-next-line unicorn/prefer-code-point
    const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const filename = response.fileName;
    const file = new File([blob], filename, { type: blob.type });
    const ppg: PPGResponseData = {
      filnamn: filename,
      ppgFiler: [file],
      avtalId: response.avtalId,
      signerandePart: response.signerandeWorksite,
      signeringsTjanstNamn: 'Egreement',
      signeradAvAllaParterDatum: response.signedDateTime,
      signerare: response.signerare.map((signer) => ({
        firstName: signer.firstName,
        lastName: signer.lastName,
        email: signer.email,
        signeradDatumTid: signer.signedDateTime,
      })),
    };
    return ppg;
  }
}

export enum SigneringServiceErrorEnum {
  DirektsigneringNotAvailable = 'DirektsigneringNotAvailable',
}
export type SigneringServiceError = keyof typeof SigneringServiceErrorEnum;

export enum EgreementStatesEnum {
  DRAFT = 'DRAFT',
  READY_TO_SEND = 'READY_TO_SEND',
  READY_TO_SIGN = 'READY_TO_SIGN',
  SIGNED = 'SIGNED',
  SIGNED_BY_ALL = 'SIGNED_BY_ALL',
  REJECTED = 'REJECTED',
  REVOKED = 'REVOKED',
  EXPIRED = 'EXPIRED',
}

/**
 * EgreementSigneringStatus represents the status of the signing process when returned from Egreement API.
 */
export enum EgreementSigneringStatus {
  Success = 'success',
}

export type SigneringStatusResponse = {
  status: EgreementStatesEnum;
  LastActivityOn: Date;
  Parties: { [key: string]: string };
};

export type SigneradDokumentResponse = {
  filename: string;
  base64data: string[];
};

export type GetSignedDocumentResponse = {
  avtalId: string;
  signerandeWorksite: string;
  signerare: {
    firstName: string;
    lastName: string;
    email: string;
    signedDateTime: Date;
  }[];
  signedDateTime: Date;
  fileName: string;
  documentContent: string; // Base64 encoded content
};

export type PPGResponseData = {
  ppgFiler?: File[];
  filnamn?: string;
  signerandePart?: string;
  avtalId?: string;
  signeringsTjanstNamn?: string;
  verifyUrl?: string;
  signeradAvAllaParterDatum?: Date;
  signerare?: {
    firstName: string;
    lastName: string;
    email: string;
    signeradDatumTid: Date;
  }[];
};

export const PPGResonseDataKeys = {
  ppgFiler: 'ppgFiler',
  filnamn: 'filnamn',
  signerandePart: 'signerandePart',
  avtalId: 'avtalId',
  signeringsTjanstNamn: 'signeringsTjanstNamn',
  verifyUrl: 'verifyUrl',
  signeradAvAllaParterDatum: 'signeradAvAllaParterDatum',
  signerare: 'signerare',
} as const;
